function getNativeFilterLabPlugin() {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.Capacitor?.Plugins?.NativeFilterLab ?? null;
  } catch {
    return null;
  }
}

/**
 * Read NativeFilterLab plugin status from Android (logging/diagnostics only).
 * Does not enable camera, filter rendering, or change call routing.
 */
export async function getNativeFilterLabStatus() {
  const plugin = getNativeFilterLabPlugin();
  if (!plugin || typeof plugin.getStatus !== "function") {
    return {
      present: false,
      ready: false,
      available: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.getStatus();
    return {
      present: result?.present === true,
      ready: result?.ready === true,
      available: result?.available === true,
      reason: String(result?.reason || "unknown"),
      plugin: result?.plugin ? String(result.plugin) : undefined,
      message: result?.message ? String(result.message) : undefined,
    };
  } catch (error) {
    return {
      present: true,
      ready: false,
      available: false,
      reason: "get_status_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open FilterLabActivity shell via native plugin (diagnostics/dev bridge only).
 * Does not start a camera, filter engine, or Zego/call session.
 */
export async function openNativeFilterLab() {
  const plugin = getNativeFilterLabPlugin();
  if (!plugin || typeof plugin.openLab !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openLab();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      shellOnly: result?.shellOnly === true,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_lab_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open NativeCameraLabActivity via native plugin (isolated Phase 1 proof).
 * CameraX front-camera preview only - no filters, no ImageAnalysis, no Zego,
 * no Snap, no billing/call logic.
 */
export async function openCameraLab() {
  const plugin = getNativeFilterLabPlugin();
  if (!plugin || typeof plugin.openCameraLab !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openCameraLab();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_camera_lab_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open SnapNativeCameraLabActivity via native plugin (isolated Snap Camera Kit
 * Android proof). Real Camera Kit Android SDK applying the luminous_femme
 * Snap lens - no Zego, no calls, no Snap Web, no billing.
 */
export async function openSnapNativeCameraLab() {
  const plugin = getNativeFilterLabPlugin();
  if (!plugin || typeof plugin.openSnapNativeCameraLab !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openSnapNativeCameraLab();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_snap_native_camera_lab_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open SnapNativeCameraActivity via native plugin (production bottom-nav
 * camera). Real Camera Kit Android SDK, no_filter/snow_radiance/
 * luminous_femme carousel with last-selected-lens persistence - no Zego, no
 * calls, no billing. Separate from openSnapNativeCameraLab(), which keeps
 * opening the QA/dev lab activity.
 */
export async function openSnapNativeCamera() {
  const plugin = getNativeFilterLabPlugin();
  if (!plugin || typeof plugin.openSnapNativeCamera !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openSnapNativeCamera();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_snap_native_camera_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open LivoraSnapFrameExtractionTestActivity via native plugin (Phase 3E-1
 * isolated diagnostic). Proves Snap Camera Kit's ImageProcessor.connectOutput
 * API delivers continuous filtered frames into an ImageReader - frames are
 * only counted/logged, never sent anywhere. No Zego, no production call logic.
 */
export async function openSnapFrameExtractionTest() {
  const plugin = getNativeFilterLabPlugin();
  if (!plugin || typeof plugin.openSnapFrameExtractionTest !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openSnapFrameExtractionTest();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_snap_frame_extraction_test_failed",
      error: String(error?.message || error),
    };
  }
}
